import { Button, Col, Form, GetProp, Input, Row, Upload, UploadProps } from 'antd';
import { useState } from 'react';
import { HexAlphaColorPicker } from 'react-colorful';
import { FormProps } from 'antd/es/form/Form';
import { useNavigate } from 'react-router-dom';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';

// services
import NotificationService from 'core/services/notification';
import { onCreateScreen } from 'modules/splashScreen/service/splashScreen';

// components
import BackButton from 'components/backButton/backButton';
import { useTranslation } from 'react-i18next';

type FieldType = {
	'title[ar]': string;
	'title[en]': string;
	'description[ar]': string;
	'description[en]': string;
	bg_color: string;
	active: '1';
	image: any;
	sort: number;
};

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

function SplashScreenCreatePage() {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [createForm] = Form.useForm();
	const [imageUrl, setImageUrl] = useState<string>();

	const onSubmit: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const createScreenPayload = new FormData();
			createScreenPayload.append('title[ar]', values['title[ar]']);
			createScreenPayload.append('title[en]', values['title[en]']);
			createScreenPayload.append('description[ar]', values['description[ar]']);
			createScreenPayload.append('description[en]', values['description[en]']);
			createScreenPayload.append('image', values.image);
			createScreenPayload.append('bg_color', values.bg_color);
			createScreenPayload.append('active', '1');

			await onCreateScreen(createScreenPayload);
			NotificationService.success('Success', 'Splash screen created successfully');
			navigate('/settings');
		} catch (error) {
			setIsSubmitting(false);
			NotificationService.error('Ops!', 'Error on adding splash screen');
		}
	};

	const onFinishFailed: FormProps<FieldType>['onFinishFailed'] = (errorInfo) => {
		createForm.scrollToField(errorInfo.errorFields[0].name, {
			behavior: (el) => {
				el.length > 0 &&
					window.scrollTo({
						top: el[0].top - 80,
						behavior: 'smooth',
					});
			},
		});
	};

	const beforeUpload = (file: FileType) => {
		const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
		if (!isJpgOrPng) {
			NotificationService.error('Only JPG/PNG format are accepted');
			return false;
		}

		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(setImageUrl(reader.result as string));
		});

		return false;
	};

	const handleChange: UploadProps['onChange'] = (info) => {
		createForm.setFieldsValue({ image: info.file });
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('SPLASH_SCREENS.ADD_SPLASH_SCREEN')}</h3>
			</div>

			<div className="sectionContainer">
				<h5 style={{ marginBottom: 10 }}>{t('SPLASH_SCREENS.INFORMATION')}</h5>

				<Form name="createForm" form={createForm} onFinish={onSubmit} onFinishFailed={onFinishFailed}>
					<Row gutter={[20, 20]}>
						<Col span={24} md={12}>
							<Form.Item<FieldType> name="title[ar]" rules={[{ required: true, message: t('SPLASH_SCREENS.ARABIC_TITLE_REQUIRED') }]}>
								<Input size="large" placeholder={t('SPLASH_SCREENS.ARABIC_TITLE')} />
							</Form.Item>
						</Col>

						<Col span={24} md={12}>
							<Form.Item<FieldType> name="title[en]" rules={[{ required: true, message: t('SPLASH_SCREENS.ENGLISH_TITLE_REQUIRED') }]}>
								<Input size="large" placeholder={t('SPLASH_SCREENS.ENGLISH_TITLE')} />
							</Form.Item>
						</Col>

						<Col span={24} md={12}>
							<Form.Item<FieldType> name="description[ar]" rules={[{ required: true, message: t('SPLASH_SCREENS.ARABIC_DESCRIPTION_REQUIRED') }]}>
								<Input.TextArea style={{ height: 200, resize: 'none' }} size="large" placeholder={t('SPLASH_SCREENS.ARABIC_DESCRIPTION')} />
							</Form.Item>
						</Col>

						<Col span={24} md={12}>
							<Form.Item<FieldType>
								name="description[en]"
								rules={[{ required: true, message: t('SPLASH_SCREENS.ENGLISH_DESCRIPTION_REQUIRED') }]}>
								<Input.TextArea
									style={{ height: 200, resize: 'none' }}
									size="large"
									placeholder={t('SPLASH_SCREENS.ENGLISH_DESCRIPTION')}
								/>
							</Form.Item>
						</Col>
					</Row>

					<div className="my-3">
						<Row gutter={[20, 20]}>
							<Col span={24} md={12}>
								<h5>{t('SPLASH_SCREENS.MEDIA')}</h5>
								<Form.Item<FieldType> name="image" hidden>
									<Input />
								</Form.Item>
								<Upload
									className="squareInput"
									listType="picture-card"
									showUploadList={false}
									beforeUpload={beforeUpload}
									onChange={handleChange}>
									{imageUrl ? (
										<img src={imageUrl} alt="avatar" style={{ width: '100%' }} />
									) : (
										<button style={{ border: 0, background: 'none', width: 200, height: 200 }} type="button">
											{isSubmitting ? <LoadingOutlined /> : <PlusOutlined />}
											<div style={{ marginTop: 8 }}>{t('COMMON.UPLOAD')}</div>
										</button>
									)}
								</Upload>
							</Col>

							<Col span={24} md={12}>
								<h5>{t('SPLASH_SCREENS.BACKGROUND_COLOR')}</h5>
								<Form.Item<FieldType> name="bg_color" hidden>
									<Input />
								</Form.Item>
								<HexAlphaColorPicker color="#fff" onChange={(value) => createForm.setFieldValue('bg_color', value)} />
							</Col>
						</Row>
					</div>
				</Form>

				<Form.Item>
					<Button
						size="middle"
						type="primary"
						htmlType="submit"
						form="createForm"
						loading={isSubmitting}
						style={{ marginTop: 20, display: 'flex' }}>
						{t('COMMON.ADD')}
					</Button>
				</Form.Item>
			</div>
		</>
	);
}

export default SplashScreenCreatePage;
